import React, { useState } from "react";
import {
  Modal,
  Select,
  Checkbox,
  InputNumber,
  Button,
  Input,
  Upload,
  Icon
} from "antd";
import { FieldArray, Formik } from "formik";
import styled from "styled-components";
import * as Yup from "yup";

import { Field } from "../../../../../components/controls/FormikForm";

const { Option } = Select;

const DOCUMENT_TYPES = [
  ["identity_document", "Documento de identidad / DNI", "idcard"],
  ["cv", "CV", "profile"],
  ["certificate", "Certificado", "safety-certificate"],
  ["degree", "Título", "read"],
  ["proof", "Constancia", "file-done"],
  ["image", "Imagen", "picture"],
  //["pdf", "PDF", "file-pdf"],
  ["form", "Formulario", "file-text"],
  ["work_document", "Documento laboral", "solution"],
  ["other", "Otro", "paper-clip"]
];

const FILE_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "jpg",
  "jpeg",
  "png",
  "xls",
  "xlsx",
  "csv"
];

const TEMPLATE_EXTENSIONS = FILE_EXTENSIONS;

const makeDocument = index => ({
  id: `document-${Date.now()}-${index}`,
  documentType: "identity_document",
  uploadMode: "single",
  allowedExtensions: ["pdf"],
  maxSizeMb: 10,
  required: true
});

const normalizeExtensions = extensions =>
  (extensions || []).map(extension => String(extension).toLowerCase());

const schema = Yup.object().shape({
  config: Yup.object().shape({
    documents: Yup.array()
      .of(
        Yup.object().shape({
          documentType: Yup.string().required("Campo obligatorio"),
          customDocumentTypeName: Yup.string().when("documentType", {
            is: "other",
            then: Yup.string().required("Campo obligatorio")
          }),
          allowedExtensions: Yup.array().min(
            1,
            "Selecciona al menos un formato"
          ),
          uploadMode: Yup.string().oneOf(["single", "front_back"]),
          maxSizeMb: Yup.number()
            .min(1)
            .max(25)
            .required("Campo obligatorio")
        })
      )
      .min(1, "Agrega al menos un documento")
  })
});

const ModalTitle = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 18px;
`;

const TitleIcon = styled.div`
  align-items: center;
  background: #edf4ff;
  border-radius: 10px;
  color: #155bff;
  display: flex;
  height: 46px;
  justify-content: center;
  width: 46px;

  .anticon {
    font-size: 26px;
  }
`;

const Title = styled.h2`
  color: #111936;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 6px;
`;

const Subtitle = styled.p`
  color: #6b7890;
  font-size: 16px;
  font-weight: 400;
  margin: 0;
`;

const TopBar = styled.div`
  align-items: center;
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr auto;
  margin: 22px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoBox = styled.div`
  align-items: center;
  background: #eef4ff;
  border-radius: 8px;
  color: #6b7890;
  display: flex;
  gap: 16px;
  padding: 14px 18px;

  .info-icon {
    align-items: center;
    background: #155bff;
    border-radius: 50%;
    color: #ffffff;
    display: flex;
    flex: 0 0 30px;
    height: 30px;
    justify-content: center;
    width: 30px;
  }

  strong {
    color: #155bff;
    display: block;
    font-size: 15px;
    margin-bottom: 2px;
  }
`;

const DocumentCard = styled.div`
  background: #ffffff;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 16px 18px 18px;
`;

const DocumentHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const DocumentTitle = styled.div`
  align-items: center;
  color: #111936;
  display: flex;
  font-size: 17px;
  font-weight: 800;
  gap: 12px;
`;

const DragHandle = styled.span`
  color: #7d8aa3;
  letter-spacing: 2px;
`;

const DeleteButton = styled(Button)`
  border: 0 !important;
  color: #ff4d4f !important;
`;

const FieldsGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(220px, 1.4fr) minmax(220px, 1.3fr) minmax(170px, 1fr) minmax(150px, 0.9fr);

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.span`
  color: #111936;
  display: block;
  font-size: 14px;
  font-weight: 800;
  margin-bottom: 8px;

  em {
    color: #ff4d4f;
    font-style: normal;
  }
`;

const HelpText = styled.span`
  color: #7d8aa3;
  display: block;
  font-size: 12px;
  line-height: 1.4;
  margin-top: 8px;
`;

const RequiredCell = styled.div`
  padding-top: 32px;

  .ant-checkbox-wrapper {
    color: #111936;
    font-weight: 800;
  }

  @media (max-width: 640px) {
    padding-top: 0;
  }
`;

const TemplateRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
`;

const TemplateUploadButton = styled(Button)`
  border: 1px dashed #155bff !important;
  border-radius: 8px !important;
  color: #155bff !important;
  font-weight: 800 !important;
  min-width: 260px;

  @media (max-width: 640px) {
    min-width: 100%;
  }
`;

const TemplateHelp = styled.span`
  color: #7d8aa3;
  font-size: 13px;
`;

const AddDocumentButton = styled(Button)`
  border: 1px dashed #8fb4ff !important;
  border-radius: 8px !important;
  color: #155bff !important;
  font-weight: 800 !important;
  min-height: 76px;
  width: 100%;

  small {
    color: #6b7890;
    display: block;
    font-weight: 400;
    margin-top: 4px;
  }
`;

const normalizeValues = values => ({
  ...values,
  config: {
    ...values.config,
    documents: values.config.documents.map(document => ({
      ...document,
      uploadMode:
        document.documentType === "identity_document"
          ? document.uploadMode || "single"
          : "single",
      allowedExtensions: normalizeExtensions(document.allowedExtensions)
    }))
  }
});

export default function DocumentRequirementModal({
  question,
  idOpportunity,
  saveQuestion,
  uploadTemplate,
  onCancel
}) {
  const [uploadingTemplate, setUploadingTemplate] = useState(false);

  const uploadFormTemplate = async ({ file, index, setFieldValue }) => {
    if (!idOpportunity || !uploadTemplate) {
      throw new Error("Template upload is not available");
    }

    setUploadingTemplate(true);
    try {
      const uploaded = await uploadTemplate({ id: idOpportunity, file });
      if (uploaded && uploaded.id) {
        setFieldValue(
          `config.documents.${index}.templateFile`,
          uploaded.location
        );
        setFieldValue(
          `config.documents.${index}.templateFileName`,
          uploaded.fileName
        );
      }
    } finally {
      setUploadingTemplate(false);
    }

    return false;
  };

  return (
    <Formik
      initialValues={
        question || {
          id: `document-upload-${Date.now()}`,
          text: "Documentación requerida",
          type: "document_upload",
          enabled: true,
          knockout: false,
          config: {
            documents: [makeDocument(0)]
          }
        }
      }
      validationSchema={schema}
      onSubmit={values => {
        const nextValues = normalizeValues(values);
        saveQuestion({
          ...nextValues,
          type: "document_upload",
          enabled: true,
          knockout: false
        });
        if (document.activeElement && document.activeElement.blur) {
          document.activeElement.blur();
        }
        onCancel();
      }}
    >
      {({ values, handleSubmit, setFieldValue }) => (
        <Modal
          visible
          width="min(1120px, calc(100vw - 32px))"
          onCancel={onCancel}
          footer={[
            <Button key="cancel" size="large" onClick={onCancel}>
              Cancelar
            </Button>,
            <Button
              key="save"
              size="large"
              type="primary"
              onClick={handleSubmit}
            >
              Guardar cambios
            </Button>
          ]}
          title={
            <ModalTitle>
              <TitleIcon>
                <Icon type="file-text" />
              </TitleIcon>
              <div>
                <Title>Requerimiento de documentación</Title>
                <Subtitle>
                  Configura los documentos que el candidato deberá subir para
                  completar este requisito.
                </Subtitle>
              </div>
            </ModalTitle>
          }
        >
          <TopBar>
            <InfoBox>
              <span className="info-icon">i</span>
              <span>
                <strong>Información importante</strong>
                Los documentos solicitados serán visibles para el candidato
                durante el proceso de aplicación.
              </span>
            </InfoBox>
          </TopBar>

          <FieldArray
            name="config.documents"
            render={arrayHelpers => (
              <>
                {values.config.documents.map((document, index) => (
                  <DocumentCard key={document.id || index}>
                    <DocumentHeader>
                      <DocumentTitle>
                        <DragHandle>::</DragHandle>
                        Documento {index + 1}
                      </DocumentTitle>
                      {values.config.documents.length > 1 && (
                        <DeleteButton
                          icon="delete"
                          type="link"
                          onClick={() => arrayHelpers.remove(index)}
                        />
                      )}
                    </DocumentHeader>

                    <FieldsGrid>
                      <div>
                        <FieldLabel>
                          Tipo de documento <em>*</em>
                        </FieldLabel>
                        <Field
                          name={`config.documents.${index}.documentType`}
                          showError={false}
                        >
                          <Select
                            onChange={documentType => {
                              setFieldValue(
                                `config.documents.${index}.documentType`,
                                documentType
                              );
                              if (documentType !== "identity_document") {
                                setFieldValue(
                                  `config.documents.${index}.uploadMode`,
                                  "single"
                                );
                              }
                            }}
                          >
                            {DOCUMENT_TYPES.map(([value, label, icon]) => (
                              <Option key={value} value={value}>
                                <Icon type={icon} /> {label}
                              </Option>
                            ))}
                          </Select>
                        </Field>
                        <HelpText>Selecciona el tipo de documento.</HelpText>
                      </div>

                      <div>
                        <FieldLabel>
                          Formatos permitidos <em>*</em>
                        </FieldLabel>
                        <Field
                          name={`config.documents.${index}.allowedExtensions`}
                          showError={false}
                          onChange={extensions =>
                            setFieldValue(
                              `config.documents.${index}.allowedExtensions`,
                              normalizeExtensions(extensions)
                            )
                          }
                        >
                          <Select mode="multiple">
                            {FILE_EXTENSIONS.map(extension => (
                              <Option key={extension} value={extension}>
                                {extension.toUpperCase()}
                              </Option>
                            ))}
                          </Select>
                        </Field>
                        <HelpText>Formatos de archivo aceptados.</HelpText>
                      </div>

                      <div>
                        <FieldLabel>
                          Tamaño máximo (MB) <em>*</em>
                        </FieldLabel>
                        <Field
                          name={`config.documents.${index}.maxSizeMb`}
                          showError={false}
                        >
                          <InputNumber min={1} max={25} />
                        </Field>
                        <HelpText>Tamaño máximo por archivo.</HelpText>
                      </div>

                      <RequiredCell>
                        <Field
                          name={`config.documents.${index}.required`}
                          valueProp="checked"
                          getValueFromEvent={event => event.target.checked}
                          showError={false}
                          containerProps={{ style: { marginBottom: 0 } }}
                        >
                          <Checkbox>Obligatorio</Checkbox>
                        </Field>
                        <HelpText>
                          El candidato debe subir este documento.
                        </HelpText>
                      </RequiredCell>
                    </FieldsGrid>

                    {document.documentType === "identity_document" && (
                      <TemplateRow>
                        <div style={{ minWidth: 260 }}>
                          <FieldLabel>Modo de carga</FieldLabel>
                          <Field
                            name={`config.documents.${index}.uploadMode`}
                            showError={false}
                          >
                            <Select>
                              <Option value="single">Un solo archivo</Option>
                              <Option value="front_back">
                                Anverso y reverso
                              </Option>
                            </Select>
                          </Field>
                          <HelpText>
                            Usa anverso y reverso cuando el DNI deba cargarse
                            en dos imágenes o archivos separados.
                          </HelpText>
                        </div>
                      </TemplateRow>
                    )}

                    {document.documentType === "other" && (
                      <Field
                        name={`config.documents.${index}.customDocumentTypeName`}
                        label="Nombre personalizado"
                      >
                        <Input maxLength={160} />
                      </Field>
                    )}

                    {document.documentType === "form" && (
                      <TemplateRow>
                        <div>
                          <FieldLabel>Plantilla</FieldLabel>
                          <Upload
                            accept={TEMPLATE_EXTENSIONS.map(
                              extension => `.${extension}`
                            ).join(",")}
                            beforeUpload={file => {
                              uploadFormTemplate({
                                file,
                                index,
                                setFieldValue
                              }).catch(() => null);
                              return false;
                            }}
                            showUploadList={false}
                          >
                            <TemplateUploadButton
                              icon="upload"
                              loading={uploadingTemplate}
                              disabled={!idOpportunity}
                            >
                              {document.templateFile
                                ? "Reemplazar plantilla"
                                : "Cargar plantilla (opcional)"}
                            </TemplateUploadButton>
                          </Upload>
                        </div>
                        <TemplateHelp>
                          {document.templateFile
                            ? document.templateFile.fileName ||
                              document.templateFileName ||
                              "Plantilla cargada"
                            : "Adjunta un archivo de plantilla para que el candidato lo descargue."}
                        </TemplateHelp>
                      </TemplateRow>
                    )}
                  </DocumentCard>
                ))}
                <AddDocumentButton
                  onClick={() =>
                    arrayHelpers.push(
                      makeDocument(values.config.documents.length)
                    )
                  }
                >
                  <Icon type="plus-circle" /> Agregar otro documento
                  <small>
                    Puedes agregar más documentos según tus necesidades.
                  </small>
                </AddDocumentButton>
              </>
            )}
          />
        </Modal>
      )}
    </Formik>
  );
}
